import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Settings,
  Save,
  Loader2,
  Shield,
  Clock,
  AlertCircle,
  Database,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Play,
  Upload,
  AlertTriangle
} from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import IpBlockTab from './IpBlockTab';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SystemConfigPage = () => {
  const { isAdmin } = useAuth();
  const [config, setConfig] = useState({
    password_expiration_days: 90,
    updated_at: null,
    updated_by: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordExpirationDays, setPasswordExpirationDays] = useState(90);
  
  // Estados para backup e migrations
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restoreStats, setRestoreStats] = useState(null);
  const fileInputRef = useRef(null);
  const [migrations, setMigrations] = useState([]);
  const [loadingMigrations, setLoadingMigrations] = useState(false);
  const [runningMigrations, setRunningMigrations] = useState(false);

  useEffect(() => {
    loadConfig();
    loadMigrations();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/system/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConfig(response.data);
      setPasswordExpirationDays(response.data.password_expiration_days);
      
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações do sistema');
    } finally {
      setLoading(false);
    }
  };

  const loadMigrations = async () => {
    setLoadingMigrations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/system/migrations/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMigrations(response.data.migrations || []);
    } catch (error) {
      console.error('Erro ao carregar migrations:', error);
    } finally {
      setLoadingMigrations(false);
    }
  };

  const handleRunMigrations = async () => {
    setRunningMigrations(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/system/migrations/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      
      if (result.applied && result.applied.length > 0) {
        toast.success(`${result.applied.length} migration(s) aplicada(s) com sucesso!`);
      } else if (result.total_pending === 0) {
        toast.info('Nenhuma migration pendente para executar');
      }
      
      if (result.failed && result.failed.length > 0) {
        toast.error(`${result.failed.length} migration(s) falharam`);
      }
      
      // Recarregar status
      await loadMigrations();
      
    } catch (error) {
      console.error('Erro ao executar migrations:', error);
      toast.error('Erro ao executar migrations');
    } finally {
      setRunningMigrations(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloadingBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/system/database/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `backup_gridprofile_${new Date().toISOString().slice(0, 10)}.zip`;

      const blob = new Blob([response.data], {
        type: response.data?.type || 'application/zip'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar backup:', error);

      let errorMsg = 'Erro ao baixar backup';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch {
          errorMsg = error.response?.statusText || errorMsg;
        }
      } else {
        errorMsg = error.response?.data?.detail || error.message || errorMsg;
      }

      toast.error(errorMsg);
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.name.endsWith('.json') && !file.name.endsWith('.zip')) {
      toast.error('Por favor, selecione um arquivo JSON (.json) ou ZIP (.zip)');
      return;
    }

    // Confirmar restauração
    const confirmMessage = restoreMode === 'replace' 
      ? '⚠️ ATENÇÃO: Modo SUBSTITUIR selecionado!\n\nIsso irá APAGAR todos os dados existentes e substituir pelo backup.\n\nDeseja continuar?'
      : 'Modo MESCLAR selecionado.\n\nIsso irá adicionar novos registros e atualizar existentes.\n\nDeseja continuar?';
    
    if (!window.confirm(confirmMessage)) {
      event.target.value = '';
      return;
    }

    setUploadingBackup(true);
    setRestoreStats(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/system/database/restore?mode=${restoreMode}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setRestoreStats(response.data.stats);
      toast.success('Backup restaurado com sucesso!');
      
      // Recarregar dados
      loadConfig();
      loadMigrations();

    } catch (error) {
      console.error('Erro ao restaurar backup:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao restaurar backup';
      toast.error(errorMsg);
    } finally {
      setUploadingBackup(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    // Validação
    if (!passwordExpirationDays || passwordExpirationDays < 1 || passwordExpirationDays > 365) {
      toast.error('O prazo deve ser entre 1 e 365 dias');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/api/system/config`,
        { password_expiration_days: parseInt(passwordExpirationDays) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConfig(response.data);
      toast.success('Configurações salvas com sucesso!');
      
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao salvar configurações';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Settings className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações do Sistema</h1>
            <p className="text-sm text-gray-600">Gerencie as configurações globais do GridProfile</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="geral">
        <TabsList className="mb-6">
          <TabsTrigger value="geral">Geral</TabsTrigger>
          {isAdmin && <TabsTrigger value="bloqueio-ip">Bloqueio por IP</TabsTrigger>}
        </TabsList>

        <TabsContent value="geral">
      {/* Card de Segurança */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <CardTitle>Configurações de Segurança</CardTitle>
          </div>
          <CardDescription>
            Configure políticas de segurança para todos os usuários do sistema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Expiração de Senha */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="passwordExpiration" className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  Senha Expira em (dias)
                </Label>
                <p className="text-sm text-gray-600">
                  Define o período de validade das senhas de todos os usuários
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <Input
                  id="passwordExpiration"
                  type="number"
                  min="1"
                  max="365"
                  value={passwordExpirationDays}
                  onChange={(e) => setPasswordExpirationDays(e.target.value)}
                  className="text-lg font-semibold"
                  disabled={saving}
                />
              </div>
              <div className="text-sm text-gray-600">
                dias
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Como funciona:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Novos usuários devem trocar senha no primeiro login</li>
                  <li>Senhas existentes expiram após o período configurado</li>
                  <li>Sistema força troca de senha automaticamente ao expirar</li>
                  <li>Valor recomendado: entre 30 e 90 dias</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Botão de Salvar */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || passwordExpirationDays === config.password_expiration_days}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </>
              )}
            </Button>

            {config.updated_at && (
              <div className="text-sm text-gray-500">
                Última atualização: {formatDate(config.updated_at)}
                {config.updated_by && ` por ${config.updated_by}`}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Card de Backup do Banco */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-green-600" />
            <CardTitle>Backup do Banco de Dados</CardTitle>
          </div>
          <CardDescription>
            Faça download de um backup completo do banco de dados em formato ZIP
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
            <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium mb-1">O backup inclui:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Usuários, funcionários e empresas</li>
                <li>Contratos e documentos de assinatura</li>
                <li>Configurações do sistema e migrations</li>
                <li>Registros de ponto e banco de horas</li>
              </ul>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadBackup}
              disabled={downloadingBackup}
              className="bg-green-600 hover:bg-green-700"
              data-testid="download-backup-btn"
            >
              {downloadingBackup ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando backup...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Backup (ZIP)
                </>
              )}
            </Button>
          </div>

          {/* Seção de Restauração */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Importar Backup
            </h4>
            
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Atenção:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Mesclar:</strong> Adiciona novos registros e atualiza existentes</li>
                  <li><strong>Substituir:</strong> Remove TODOS os dados e substitui pelo backup</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <Label className="text-sm font-medium">Modo de restauração:</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === 'merge'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Mesclar</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="replace"
                    checked={restoreMode === 'replace'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-red-600 font-medium">Substituir (⚠️ Apaga tudo)</span>
                </label>
              </div>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleRestoreBackup}
              accept=".json,.zip"
              className="hidden"
              data-testid="restore-backup-input"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingBackup}
              variant={restoreMode === 'replace' ? 'destructive' : 'outline'}
              data-testid="restore-backup-btn"
            >
              {uploadingBackup ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Restaurando backup...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Selecionar arquivo ZIP
                </>
              )}
            </Button>

            {/* Estatísticas de restauração */}
            {restoreStats && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="font-medium text-blue-900 mb-2">Resultado da restauração:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-500">Coleções:</span>
                    <span className="font-bold ml-1">{restoreStats.collections_processed}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-green-600">Inseridos:</span>
                    <span className="font-bold ml-1">{restoreStats.documents_inserted}</span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-blue-600">Atualizados:</span>
                    <span className="font-bold ml-1">{restoreStats.documents_updated}</span>
                  </div>
                  {restoreStats.documents_deleted > 0 && (
                    <div className="bg-white p-2 rounded">
                      <span className="text-red-600">Removidos:</span>
                      <span className="font-bold ml-1">{restoreStats.documents_deleted}</span>
                    </div>
                  )}
                </div>
                {restoreStats.errors && restoreStats.errors.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    <p className="font-medium">Erros ({restoreStats.errors.length}):</p>
                    <ul className="list-disc list-inside">
                      {restoreStats.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Card de Migrations */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-600" />
              <CardTitle>Migrations do Banco</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadMigrations}
              disabled={loadingMigrations}
            >
              {loadingMigrations ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
          <CardDescription>
            Gerencie as atualizações estruturais do banco de dados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Lista de Migrations */}
          <div className="border rounded-lg divide-y">
            {migrations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {loadingMigrations ? 'Carregando...' : 'Nenhuma migration encontrada'}
              </div>
            ) : (
              migrations.map((migration) => (
                <div key={migration.name} className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {migration.applied ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium text-sm">{migration.name}</p>
                      {migration.applied && migration.applied_info?.applied_at && (
                        <p className="text-xs text-gray-500">
                          Aplicada em: {new Date(migration.applied_info.applied_at).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    migration.applied 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {migration.applied ? 'Aplicada' : 'Pendente'}
                  </span>
                </div>
              ))
            )}
          </div>
          
          {/* Botão para executar migrations pendentes */}
          {migrations.some(m => !m.applied) && (
            <Button
              onClick={handleRunMigrations}
              disabled={runningMigrations}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="run-migrations-btn"
            >
              {runningMigrations ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Executando migrations...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Executar Migrations Pendentes
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Card Informativo */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Importante:</p>
              <ul className="space-y-1">
                <li>• As alterações afetam todos os usuários do sistema imediatamente</li>
                <li>• Senhas que já expiraram continuarão exigindo troca no próximo login</li>
                <li>• É recomendado informar os usuários antes de reduzir drasticamente o prazo</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="bloqueio-ip">
            <IpBlockTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SystemConfigPage;
